import * as Sentry from '@sentry/react';
import { matchPath } from 'react-router-dom';

import { API_HOST } from './react-web-constants';
import { history } from './history';

const dsn = process.env.REACT_APP_SENTRY_DSN;
const isDevelopment = process.env.NODE_ENV === 'development';
let sentryInitialized = false;

// Sample rates default to a conservative production setup:
// - traces: 0       -> no performance transactions by default
// - session replay: 0 -> no always-on replay recording by default
// - replay on error: 1 -> capture replay context for error sessions
//
// These can still be overridden with env vars if we ever want different
// behavior per environment, but the intent is that most deployments can rely
// on the built-in defaults without having to configure them explicitly.
//
// In local development we force all sample rates to 0. That keeps Sentry
// active for manual testing and error visibility without generating tracing
// or replay volume from day-to-day dev work.
function parseSampleRate(value, fallback) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Optional overrides:
// - REACT_APP_SENTRY_TRACES_SAMPLE_RATE
// - REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
// - REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
//
// Example customization:
// - staging can set traces to 1.0 for full performance visibility
// - production can keep traces/replays low to control volume and cost
// - a troubleshooting deploy can temporarily raise replay session sampling
const tracesSampleRate = isDevelopment ? 0 : parseSampleRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0);
const replaysSessionSampleRate = isDevelopment ? 0 : parseSampleRate(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0);
const replaysOnErrorSampleRate = isDevelopment ? 0 : parseSampleRate(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1);

function getIntegrations() {
  const integrations = [
    Sentry.reactRouterV5BrowserTracingIntegration({
      history,
      matchPath,
    }),
  ];

  // Replay is only enabled when one of the replay sample rates is non-zero.
  // With the defaults above, that means replay stays off for normal sessions
  // but turns on for sessions where an error is captured.
  if (replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0) {
    integrations.push(Sentry.replayIntegration());
  }

  return integrations;
}

export function initSentry() {
  if (!dsn || sentryInitialized || Sentry.isInitialized()) {
    return;
  }

  Sentry.init({
    dsn,
    integrations: getIntegrations(),
    environment: process.env.NODE_ENV,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    tracePropagationTargets: API_HOST ? [/^\//, API_HOST] : [/^\//],
  });

  sentryInitialized = true;
}

export async function shutdownSentry() {
  if (!sentryInitialized && !Sentry.isInitialized()) {
    return;
  }

  Sentry.setUser(null);
  await Sentry.close(2000);
  sentryInitialized = false;
}

export function setSentryUser(userAttributes = {}) {
  if (!dsn || !sentryInitialized || !Sentry.isInitialized()) {
    return;
  }

  const sentryUserId = userAttributes?.uuid;

  if (!sentryUserId) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: sentryUserId });

  // If we ever want more filtering in Sentry without changing the user shape,
  // we can attach lightweight tags here. Example:
  // Sentry.setTag('league', userAttributes?.preferredLeague || 'unknown');
  // Tags are useful for slicing issues in the Sentry UI, but we should keep
  // them low-cardinality so they stay readable and cost-effective.
}
